import type { GameClient } from "../net/GameClient";

/**
 * HitMarker — flashes a small X over the crosshair when the local
 * player confirms a hit. White on regular hit, red + larger pop when
 * the hit was the killing blow.
 *
 * Self-contained DOM overlay (no PlayCanvas dependency). Listens to
 * `onHit` and ignores anything where the local player isn't the
 * attacker.
 */
export class HitMarker {
  private readonly client: GameClient;
  private readonly root: HTMLDivElement;
  private hideHandle = 0;
  private readonly unsubs: Array<() => void> = [];

  /** Visible lifetime (ms) — keep snappy so back-to-back hits read. */
  private static readonly FLASH_MS = 110;
  /** Kill marker stays a touch longer so the read is unambiguous. */
  private static readonly KILL_FLASH_MS = 220;

  constructor(client: GameClient) {
    this.client = client;
    this.root = document.createElement("div");
    this.root.id = "hit-marker";
    this.root.innerHTML = `
      <span class="hit-marker-stroke hit-marker-tl"></span>
      <span class="hit-marker-stroke hit-marker-tr"></span>
      <span class="hit-marker-stroke hit-marker-bl"></span>
      <span class="hit-marker-stroke hit-marker-br"></span>
    `;
    document.body.appendChild(this.root);
    this.injectStyles();

    this.unsubs.push(
      this.client.onHit((msg) => {
        if (msg.attackerSessionId !== this.client.sessionId) return;
        const isKill = msg.victimHealthAfter <= 0;
        this.flash(isKill);
      }),
    );
  }

  destroy(): void {
    window.clearTimeout(this.hideHandle);
    for (const u of this.unsubs) u();
    this.root.remove();
  }

  private flash(kill: boolean): void {
    this.root.classList.remove("show", "kill");
    // Force reflow so the CSS animation restarts on back-to-back hits.
    void this.root.offsetWidth;
    this.root.classList.add("show");
    if (kill) this.root.classList.add("kill");
    window.clearTimeout(this.hideHandle);
    this.hideHandle = window.setTimeout(
      () => this.root.classList.remove("show", "kill"),
      kill ? HitMarker.KILL_FLASH_MS : HitMarker.FLASH_MS,
    );
  }

  private injectStyles(): void {
    if (document.getElementById("hit-marker-styles")) return;
    const style = document.createElement("style");
    style.id = "hit-marker-styles";
    style.textContent = `
      #hit-marker {
        position: fixed;
        top: 50%;
        left: 50%;
        width: 18px;
        height: 18px;
        transform: translate(-50%, -50%);
        pointer-events: none;
        z-index: 6;
        opacity: 0;
        transition: opacity 60ms linear;
      }
      #hit-marker.show {
        opacity: 1;
      }
      #hit-marker.kill {
        transform: translate(-50%, -50%) scale(1.5);
      }
      #hit-marker .hit-marker-stroke {
        position: absolute;
        width: 5px;
        height: 1px;
        background: rgba(255, 255, 255, 0.95);
        box-shadow: 0 0 2px rgba(0, 0, 0, 0.85);
      }
      #hit-marker.kill .hit-marker-stroke {
        background: #ff3a3a;
        box-shadow: 0 0 4px rgba(255, 0, 0, 0.7);
      }
      /* Four diagonal strokes forming an X around the center. */
      #hit-marker .hit-marker-tl { top: 3px; left: 3px;   transform: rotate(45deg);  transform-origin: left top; }
      #hit-marker .hit-marker-tr { top: 3px; right: 3px;  transform: rotate(-45deg); transform-origin: right top; }
      #hit-marker .hit-marker-bl { bottom: 3px; left: 3px;   transform: rotate(-45deg); transform-origin: left bottom; }
      #hit-marker .hit-marker-br { bottom: 3px; right: 3px;  transform: rotate(45deg);  transform-origin: right bottom; }
    `;
    document.head.appendChild(style);
  }
}
